import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const MP_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES',
};

interface Account { id: string; name: string; region: string; is_active?: number }
interface SyncRun {
  id: string; workflow: string; marketplace: string; startedAt: string;
  status: string; rowsProcessed: number; errorMessage: string | null;
}

export function PpcSettings() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [selectedMp, setSelectedMp] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const accts = await api.get<Account[]>('/accounts');
        const active = accts.filter(a => a.is_active !== 0);
        setAccounts(active);

        // Load sync runs from all accounts
        const allRuns: SyncRun[] = [];
        for (const a of active) {
          const res = await api.get<{ runs: SyncRun[] }>(`/ppc/sync-runs/${a.id}?limit=30`).catch(() => ({ runs: [] }));
          allRuns.push(...res.runs);
        }
        allRuns.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
        setSyncRuns(allRuns);
      } catch { /* */ }
      setLoading(false);
    })();
  }, []);

  async function handleSync() {
    setSyncing(true); setSyncMsg('');
    try {
      const results = await Promise.all(
        accounts.map(a => api.post<{ message: string }>(`/ppc/sync-trigger/${a.id}`).catch(() => ({ message: 'Failed' }))),
      );
      setSyncMsg(results.map((r, i) => `${accounts[i]?.name}: ${r.message}`).join(' | '));
    } catch (e) { setSyncMsg(String(e)); }
    setSyncing(false);
  }

  const filteredRuns = selectedMp
    ? syncRuns.filter(r => r.marketplace === selectedMp)
    : syncRuns;

  const marketplaces = [...new Set(syncRuns.map(r => r.marketplace))].sort();

  const statusCounts = { completed: 0, failed: 0, partial: 0 };
  for (const r of syncRuns) {
    if (r.status === 'completed') statusCounts.completed++;
    else if (r.status === 'failed') statusCounts.failed++;
    else statusCounts.partial++;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PPC Settings</h1>
          <p className="mt-1 text-sm text-gray-500">Sync configuration, engine schedule, and sync history</p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {syncing ? 'Syncing...' : 'Sync All Now'}
        </button>
      </div>

      {syncMsg && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{syncMsg}</div>}

      {/* Engine Schedule */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Automation Engine Schedule</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ScheduleCard time="6 AM" label="PPC Data Sync" desc="Pulls campaigns, keywords, search terms, performance from Amazon Ads API" />
          <ScheduleCard time="8 AM Sun" label="Recalibration" desc="Re-requests 14 days to correct daily snapshot drift" />
          <ScheduleCard time="10 AM" label="Automation Engine" desc="Evaluates all active rules, Workers AI validates, creates actions" />
          <ScheduleCard time="10 AM" label="AI Advisor" desc="Suggests new rules and tunes existing ones based on outcome data" />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-100 p-3">
            <p className="text-xs font-medium text-gray-500">Trust Levels</p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-600">manual</span>
                <span className="text-gray-500">Preview only — no auto-execution</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-yellow-100 px-1.5 py-0.5 font-mono text-yellow-700">semi</span>
                <span className="text-gray-500">Workers AI validates before execution</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-green-100 px-1.5 py-0.5 font-mono text-green-700">full</span>
                <span className="text-gray-500">Auto-execute without validation</span>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 p-3">
            <p className="text-xs font-medium text-gray-500">AI Validation</p>
            <p className="mt-1 text-xs text-gray-600">Llama 3.3 70B reviews every bid/budget change in "semi" trust mode. Rejects obviously bad changes (e.g., cutting bids on top performers).</p>
          </div>
          <div className="rounded-lg border border-gray-100 p-3">
            <p className="text-xs font-medium text-gray-500">Attribution Lag</p>
            <p className="mt-1 text-xs text-gray-600">Default 2 days of recent data ignored in analysis. Amazon attributes sales up to 14 days after click — recent data is incomplete.</p>
          </div>
        </div>
      </div>

      {/* Sync Status Summary */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total Sync Runs</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{syncRuns.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Completed</p>
          <p className="mt-1 text-2xl font-bold text-green-600">{statusCounts.completed}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Failed</p>
          <p className="mt-1 text-2xl font-bold text-red-600">{statusCounts.failed}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Partial</p>
          <p className="mt-1 text-2xl font-bold text-yellow-600">{statusCounts.partial}</p>
        </div>
      </div>

      {/* Marketplace filter */}
      {marketplaces.length > 1 && (
        <div className="flex gap-2">
          <button onClick={() => setSelectedMp(null)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${!selectedMp ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            All Markets
          </button>
          {marketplaces.map(mp => (
            <button key={mp} onClick={() => setSelectedMp(mp === selectedMp ? null : mp)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${selectedMp === mp ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {MP_NAMES[mp] ?? mp}
            </button>
          ))}
        </div>
      )}

      {/* Sync History Table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Recent Sync Runs</h3>
        </div>
        {loading ? (
          <div className="py-8 text-center text-gray-400">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Workflow</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Market</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Started</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Rows</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredRuns.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No sync runs found</td></tr>
                )}
                {filteredRuns.slice(0, 50).map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-700">{r.workflow}</td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                        {MP_NAMES[r.marketplace] ?? r.marketplace}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(r.startedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        r.status === 'completed' ? 'bg-green-100 text-green-700' :
                        r.status === 'failed' ? 'bg-red-100 text-red-700' :
                        r.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.rowsProcessed || '--'}</td>
                    <td className="px-3 py-2 text-xs text-red-500 max-w-[300px] truncate" title={r.errorMessage ?? ''}>
                      {r.errorMessage ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleCard({ time, label, desc }: { time: string; label: string; desc: string }) {
  return (
    <div className="rounded-lg border border-gray-100 p-3">
      <div className="flex items-center gap-2">
        <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-bold text-brand-700">{time}</span>
        <span className="text-sm font-medium text-gray-800">{label}</span>
      </div>
      <p className="mt-1.5 text-xs text-gray-500">{desc}</p>
    </div>
  );
}
